import objFactory from "_src/engine/factories/object";
import Camera from "_src/engine/camera";
import FrameBuffer from "_src/engine/framebuffer";
import primitiveFactory from "_src/engine/factories/primitive";
import {
  primitiveType,
  blendFactor,
  pixelBufferType,
  stencilFunc,
  stencilValue,
  attachmentId
} from "_src/engine/constants";

import Pass from "_src/render_graph/pass";
import resourceManager from "_src/render_graph/resource_manager";
import ScreenShader from "_src/shaders/screen/screen";
import OutlineShader from "_src/shaders/outline/outline";
import CopyShader from "_src/shaders/copy/copy";
import BlurShader from "_src/shaders/blur/blur";
import config from "_src/config";
import {screenUtils} from "_src/client/utils";

const BLUR_SCENE_SIZE = 0.5;

export const OBJECT_HIGHLIGHT = 0;
export const CAD_HIGHLIGHT = 1;

export default class HighlightPass extends Pass {

  constructor(attachmentsStorage, camera) {

    super(attachmentsStorage);

    this.glowEffect = false;

    this.init(camera);

  }

  init(camera) {

    const maskScene = this._addScene(camera);
    const highlightScene = this._addScene(camera);

    maskScene.clearedStencil = false;
    maskScene.clearedDepth = false;

    highlightScene.clearedStencil = false;
    highlightScene.clearedDepth = false;

    // Mask
    const maskShader = new ScreenShader({transparent : true, depthTest: false});
    maskShader.enableStencilTest();
    maskShader.setStencilFunction(stencilFunc.EQUAL);
    maskShader.setStencilReferenceValue(stencilValue.HIGHLIGHTED_OBJECT);

    const maskScreenPlane = objFactory.createDrawable(maskShader);
    maskScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    maskScreenPlane.setDiffuseColor(1.0, 1.0, 1.0);
    maskScene.add(maskScreenPlane);

    // Outline
    const width = 1 * screenUtils.getPixelRatio();
    const widthGlow = 3 * screenUtils.getPixelRatio();
    const outlineShader = new OutlineShader({transparent : true,
                                             width: this.glowEffect ? widthGlow : width,
                                             depthTest: false,
                                             outlineOnly: !this.glowEffect});

    const highlightScreenPlane = objFactory.createDrawable(outlineShader);
    highlightScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    highlightScene.add(highlightScreenPlane);

    if (this.glowEffect) {
      this._createGlowScenes(camera);
    } else {
      highlightScene.clearedColor = false;
    }

  }

  update() {

    const scenes = this.getScenes();
    const width = scenes[0].camera.viewport[2];
    const height = scenes[0].camera.viewport[3];

    if (this.glowEffect) {

      const { maskFramebuffer, outlineFramebuffer,
              blurHorFramebuffer, blurVertFramebuffer,
              combineFramebuffer } = this._initGlowFramebuffers(width, height);

      scenes[0].setFramebuffer(maskFramebuffer);
      scenes[1].setFramebuffer(outlineFramebuffer);
      scenes[2].setFramebuffer(blurHorFramebuffer);
      scenes[3].setFramebuffer(blurVertFramebuffer);
      scenes[4].setFramebuffer(combineFramebuffer);

    } else {

      const { maskFramebuffer, outlineFramebuffer } = this._initFramebuffers(width, height);

      scenes[0].setFramebuffer(maskFramebuffer);
      scenes[1].setFramebuffer(outlineFramebuffer);

    }

  }

  resize(width, height) {

    const scenes = this.getScenes();

    scenes[0].resize(width, height);
    scenes[1].resize(width, height);

    if (this.glowEffect) {
      scenes[2].resize(width * BLUR_SCENE_SIZE, height * BLUR_SCENE_SIZE);
      scenes[3].resize(width * BLUR_SCENE_SIZE, height * BLUR_SCENE_SIZE);
      scenes[4].resize(width, height);
    }

  }

  _initFramebuffers(width, height) {

    const inputColor = this.attachmentsStorage[attachmentId.BASE_COLOR_TEXTURE];
    const mainDepthStencil = this.attachmentsStorage[attachmentId.WORKSPACE_DEPTH_STENCIL];

    const maskColor = resourceManager.initBuffer(pixelBufferType.T_RGBA, { width, height });

    const maskFramebuffer = new FrameBuffer([maskColor], mainDepthStencil);
    const outlineFramebuffer = new FrameBuffer([inputColor], mainDepthStencil);

    const outlineScene = this.getSecondScene();
    const outlineScreenPlane = this.getScreenQuad(outlineScene);
    outlineScreenPlane.setTexture(maskColor, 0);

    return { maskFramebuffer, outlineFramebuffer };

  }

  _createGlowScenes(camera) {

    const halfSizeCamera = new Camera();
    const blurHorScene = this._addScene(halfSizeCamera);
    const blurVertScene = this._addScene(halfSizeCamera);
    const combineScene = this._addScene(camera);

    blurHorScene.clearedDepth = false;
    blurVertScene.clearedDepth = false;
    combineScene.clearedColor = false;
    combineScene.clearedStencil = false;

    // Horizontal Blur
    const blurHorShader = new BlurShader({horDir: true});
    const blurHorScreenPlane = objFactory.createDrawable(blurHorShader);
    blurHorScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    blurHorScene.add(blurHorScreenPlane);

    // Vertical Blur
    const blurVertShader = new BlurShader({horDir: false});
    const blurVertScreenPlane = objFactory.createDrawable(blurVertShader);
    blurVertScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    blurVertScene.add(blurVertScreenPlane);

    // Combine
    const combineShader = new CopyShader({transparent : true});
    combineShader.setSourceBlendFactor(blendFactor.ONE);
    const combineScreenPlane = objFactory.createDrawable(combineShader);
    combineScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    combineScene.add(combineScreenPlane);

    combineShader.enableStencilTest();
    combineShader.setStencilFunction(stencilFunc.NOTEQUAL);
    combineShader.setStencilReferenceValue(stencilValue.HIGHLIGHTED_OBJECT);

  }

  _initGlowFramebuffers(width, height) {

    const scenes = this.getScenes();

    const inputColor = this.attachmentsStorage[attachmentId.BASE_COLOR_TEXTURE];
    const mainDepthStencil = this.attachmentsStorage[attachmentId.WORKSPACE_DEPTH_STENCIL];

    // Mask
    const maskColor = resourceManager.initBuffer(pixelBufferType.T_RGBA, { width, height });
    const maskFramebuffer = new FrameBuffer([maskColor], mainDepthStencil);

    // Outline
    const outlineColor = resourceManager.initBuffer(pixelBufferType.T_RGBA, { width, height });

    const outlineFramebuffer = new FrameBuffer([outlineColor], mainDepthStencil);
    const outlineScene = scenes[1];
    const outlineScreenPlane = this.getScreenQuad(outlineScene);
    outlineScreenPlane.setTexture(maskColor, 0);

    // Horizontal Blur
    const blurHorColor = resourceManager.initBuffer(pixelBufferType.T_RGBA, { width: width * BLUR_SCENE_SIZE,
                                                                              height: height * BLUR_SCENE_SIZE });
    const blurHorFramebuffer = new FrameBuffer([blurHorColor], null);
    const blurHorScene = scenes[2];
    const blurHorScreenPlane = this.getScreenQuad(blurHorScene);
    blurHorScreenPlane.setTexture(outlineColor, 0);

    // Vertical Blur
    const blurVertColor = resourceManager.initBuffer(pixelBufferType.T_RGBA, { width: width * BLUR_SCENE_SIZE,
                                                                               height: height * BLUR_SCENE_SIZE });
    const blurVertFramebuffer = new FrameBuffer([blurVertColor], null);
    const blurVertScene = scenes[3];
    const blurVertScreenPlane = this.getScreenQuad(blurVertScene);
    blurVertScreenPlane.setTexture(blurHorColor, 0);

    // Combine
    const combineFramebuffer = new FrameBuffer([inputColor], mainDepthStencil);

    const combineScene = this.getLastScene();
    const combineScreenPlane = this.getScreenQuad(combineScene);
    combineScreenPlane.setTexture(blurVertColor, 0);

    return { maskFramebuffer, outlineFramebuffer, blurHorFramebuffer, blurVertFramebuffer, combineFramebuffer };

  }

  setHighlightSettings(mode) {
    const outlineColor = mode === OBJECT_HIGHLIGHT ? config.outlineColor : config.cadOutlineColor;
    const highlightScene = this.getSecondScene();
    const highlightScreenPlane = this.getScreenQuad(highlightScene);
    highlightScreenPlane.setDiffuseColor(outlineColor.r, outlineColor.g, outlineColor.b);
  }

}
